/**
 * useDeviceLayout Hook
 *
 * Centralized hook for device detection and responsive behavior.
 * Provides all device-related helpers in a single, convenient interface.
 *
 * This hook eliminates the need to repeatedly call useResponsive() and
 * helper functions across components (DRY principle).
 *
 * @example
 * ```tsx
 * // Instead of:
 * const { layoutMode, inputMode, hasHover } = useResponsive();
 * const isMobile = isMobileLayout(layoutMode);
 * const isTouch = isTouchInput(inputMode);
 *
 * // Use this:
 * const { isMobile, isTouch, hasHover } = useDeviceLayout();
 * ```
 */

import { useResponsive } from './useResponsive';
import {
  isMobileLayout,
  isDesktopLayout,
  isTouchInput,
  isMouseInput,
} from '../utils/deviceDetection';

export const useDeviceLayout = () => {
  const { layoutMode, inputMode, hasHover, width, height } = useResponsive();

  return {
    // Layout helpers - Based on screen size
    layoutMode,
    isMobile: isMobileLayout(layoutMode),
    isDesktop: isDesktopLayout(layoutMode),

    // Input helpers - Based on device capabilities
    inputMode,
    isTouch: isTouchInput(inputMode),
    isMouse: isMouseInput(inputMode),

    // Capabilities
    hasHover,

    // Screen dimensions (reactive to resize)
    width,
    height,
  };
};
